import React from "react";
import { Check, CreditCard } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/app/components/ui/button";

import { PLAN_TIERS } from "./constants";
import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { ContentSection } from "./shared";

function getStatusBadge(status) {
	switch (status) {
		case "paid":
			return <span className="rounded-md bg-[#dff7ea] px-2 py-1 text-xs font-medium text-[#017336]">Paid</span>;
		case "pending":
			return <span className="rounded-md bg-[#ffecc7] px-2 py-1 text-xs font-medium text-[#985508]">Pending</span>;
		default:
			return <span className="rounded-md bg-[#ffe1e1] px-2 py-1 text-xs font-medium text-[#b30000]">Failed</span>;
	}
}

export default function BillingTab() {
	const { plan, billingHistory } = useCompanyDashboard();
	const currentPlan = PLAN_TIERS.find((tier) => tier.id === plan) || PLAN_TIERS[0];
	const currentPlanIndex = PLAN_TIERS.findIndex((tier) => tier.id === currentPlan.id);
	const upgradePlans = PLAN_TIERS.filter((tier, index) => index > currentPlanIndex);

	return (
		<ContentSection companyHeader>
			<div className="mx-auto max-w-4xl space-y-6">
				<div className="rounded-lg border border-border bg-white p-4">
					<h3 className="mb-4 flex items-center gap-2 text-sm font-semibold">
						<CreditCard className="h-4 w-4 text-[hsl(221,83%,53%)]" />
						Current Plan
					</h3>
					<div className="flex items-center justify-between rounded-lg border border-[hsl(214,95%,93%)] bg-[hsl(214,100%,97%)] p-4">
						<div>
							<p className="text-lg font-semibold text-foreground">{currentPlan.name}</p>
							<p className="text-sm text-muted-foreground">
								{currentPlan.price} TZS{currentPlan.period === "TZS / month" ? "/month" : currentPlan.period}
							</p>
						</div>
						<div className="text-right">
							<p className="text-xs text-muted-foreground">Next billing date</p>
							<p className="text-sm font-medium">Jan 1, 2025</p>
						</div>
					</div>
				</div>

				{plan !== "enterprise" && (
					<div className="rounded-lg border border-border bg-white p-4">
						<h3 className="mb-4 text-sm font-semibold">Upgrade Plan</h3>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							{upgradePlans.map((upgradePlan) => (
								<div
									key={upgradePlan.id}
									className="rounded-lg border border-border p-4 transition-colors hover:border-[hsl(221,83%,53%)]"
								>
									<div className="mb-3 flex items-start justify-between">
										<div>
											<p className="font-semibold text-foreground">{upgradePlan.name}</p>
											<p className="text-sm text-muted-foreground">
												{upgradePlan.price} TZS{upgradePlan.period === "TZS / month" ? "/month" : upgradePlan.period}
											</p>
										</div>
									</div>
									<ul className="mb-4 space-y-1.5">
										{upgradePlan.features
											.filter((feature) => feature.included)
											.map((feature) => (
												<li key={feature.text} className="flex items-center gap-2 text-sm text-muted-foreground">
													<Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
													{feature.text}
												</li>
											))}
									</ul>
									<Button
										size="sm"
										className="w-full"
										onClick={() => {
											toast.info("Plan upgrades are not yet wired to a live billing backend in the professional app.");
										}}
									>
										Upgrade
									</Button>
								</div>
							))}
						</div>
					</div>
				)}

				<div className="rounded-lg border border-border bg-white p-4">
					<h3 className="mb-4 text-sm font-semibold">Billing History</h3>
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead>
								<tr className="border-b border-border">
									<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
										Invoice
									</th>
									<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
										Date
									</th>
									<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
										Status
									</th>
									<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
										Amount
									</th>
									<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
										Plan
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-border">
								{billingHistory.map((item) => (
									<tr key={item.invoice} className="hover:bg-secondary/50">
										<td className="px-4 py-3">
											<span className="cursor-pointer text-sm font-medium text-[hsl(221,83%,53%)] hover:underline">
												{item.invoice}
											</span>
										</td>
										<td className="px-4 py-3 text-sm text-muted-foreground">{item.date}</td>
										<td className="px-4 py-3">{getStatusBadge(item.status)}</td>
										<td className="px-4 py-3 text-sm font-medium text-foreground">{item.amount}</td>
										<td className="px-4 py-3 text-sm text-muted-foreground">{item.plan}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</ContentSection>
	);
}
